import { Button } from "components/common/buttons/Button";
import { cn } from "utils/cn";

export interface QueryLoadErrorProps {
  /** What failed to load, as the user reads it: "Couldn't load this scene's notes". */
  message: string;
  /** Runs the failed request again. */
  onRetry: () => void;
  /** True while the retried request runs. */
  isRetrying?: boolean;
  /** `block` stands in for a whole list or pane; `inline` is one row beside content that loaded. */
  layout?: "block" | "inline";
  className?: string;
}

/** A failed load handed to a component that shows it: what failed and how to retry it. */
export type QueryLoadFailure = Pick<QueryLoadErrorProps, "message" | "onRetry" | "isRetrying">;

/**
 * A failed request in place of the data it would have shown. A failure is unknown data, so it
 * never reads as an empty list, a zero or a missing item, and it always offers Try again.
 */
export const QueryLoadError = ({
  message,
  onRetry,
  isRetrying = false,
  layout = "block",
  className,
}: QueryLoadErrorProps) => (
  <div
    role="alert"
    className={cn(
      layout === "block"
        ? "flex flex-col items-center gap-3 px-4 py-8 text-center"
        : "flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2",
      className
    )}
  >
    <p className="text-sm text-muted-foreground">{message}</p>
    <Button
      type="button"
      variant="badge"
      color="gray"
      size="sm"
      isLoading={isRetrying}
      onClick={onRetry}
    >
      Try again
    </Button>
  </div>
);
